import { z } from 'zod';
import { baseMessageSchema, createMessageId } from './_base';
import { matchmakingPlayerSchema } from './shared';
import { matchmakingSessionPresetSchema } from './matchmaking';
import { gameInstanceHostReportSchema, gameInstanceIndexSchema, machineIdSchema } from '../types/gameInstance';

/**
 * Matchmaking V2 — game-instance-aware matchmaking namespace.
 *
 * Vocabulary (docs/architecture/matchmaking-v2.md):
 * - match request: a player's (or party's) request to be matched (v1: "invitation")
 * - game instance: one independently-matchmakable game slot on a host machine
 *
 * Instance identity (machineId / gameInstanceIndex) is only ever exchanged
 * between host and API. Player-facing payloads are `.strict()` so instance
 * identity can neither leak to nor be injected by a player client.
 *
 * Contract frozen by docs/feature-plans/game-instance-v2/PHASE_1.md.
 * Match-end + contract-carriage + machine capacity added by
 * docs/feature-plans/pool-match-lifecycle/PHASE_1.md.
 */
export enum MatchmakingV2MessageType {
  // Host ↔ API
  MATCHMAKING_V2_HOST_STATE_REPORT = 'MATCHMAKING_V2_HOST_STATE_REPORT',
  MATCHMAKING_V2_MATCH_ASSIGNED = 'MATCHMAKING_V2_MATCH_ASSIGNED',
  MATCHMAKING_V2_HOST_MATCH_READY = 'MATCHMAKING_V2_HOST_MATCH_READY',
  MATCHMAKING_V2_CAMPAIGN_SWITCH = 'MATCHMAKING_V2_CAMPAIGN_SWITCH',
  MATCHMAKING_V2_MATCH_ENDED = 'MATCHMAKING_V2_MATCH_ENDED',
  MATCHMAKING_V2_HOST_CAPACITY_CONFIG = 'MATCHMAKING_V2_HOST_CAPACITY_CONFIG',
  MATCHMAKING_V2_ADMIN_RESTART_INSTANCE = 'MATCHMAKING_V2_ADMIN_RESTART_INSTANCE',

  // Player → API
  MATCHMAKING_V2_CREATE_MATCH_REQUEST = 'MATCHMAKING_V2_CREATE_MATCH_REQUEST',
  MATCHMAKING_V2_JOIN_MATCH_REQUEST = 'MATCHMAKING_V2_JOIN_MATCH_REQUEST',
  MATCHMAKING_V2_LEAVE_MATCH_REQUEST = 'MATCHMAKING_V2_LEAVE_MATCH_REQUEST',
  MATCHMAKING_V2_ADD_TO_QUEUE = 'MATCHMAKING_V2_ADD_TO_QUEUE',

  // API → Player
  MATCHMAKING_V2_MATCH_REQUEST_CREATED = 'MATCHMAKING_V2_MATCH_REQUEST_CREATED',
  MATCHMAKING_V2_MATCH_REQUEST_UPDATED = 'MATCHMAKING_V2_MATCH_REQUEST_UPDATED',
  MATCHMAKING_V2_MATCH_FOUND = 'MATCHMAKING_V2_MATCH_FOUND',
  MATCHMAKING_V2_MATCH_READY = 'MATCHMAKING_V2_MATCH_READY',
  MATCHMAKING_V2_MATCH_END = 'MATCHMAKING_V2_MATCH_END',
  MATCHMAKING_V2_MATCH_FAILED = 'MATCHMAKING_V2_MATCH_FAILED',
  MATCHMAKING_V2_QUEUE_STATUS = 'MATCHMAKING_V2_QUEUE_STATUS',

  // Shared
  MATCHMAKING_V2_ERROR = 'MATCHMAKING_V2_ERROR',
  MATCHMAKING_V2_STATE_UPDATE = 'MATCHMAKING_V2_STATE_UPDATE',
}

// =============================================================================
// Payload schemas — Host ↔ API
// =============================================================================

/** Host → API: machine-level state plus one report per game instance. */
const hostStateReportPayloadSchema = z.object({
  machineId: machineIdSchema,
  machine: z.object({
    geohash: z.string().optional(),
    coords: z.object({ lat: z.number(), lng: z.number() }).optional(),
    isCloudHost: z.boolean(),
    guestAccount: z.boolean(),
    maxPlayers: z.number().int().positive().optional(),
    /**
     * Applied CONFIG maxPlayers (pre-clamp), distinct from `maxPlayers` which
     * is the clamped effective value (android-multi-instance Phase 8, review
     * fix S2): the API clears its desired-capacity marker against this field,
     * falling back to `maxPlayers` for older hosts that omit it.
     */
    configuredMaxPlayers: z.number().int().positive().optional(),
    /** Pre-created VMs on this machine (android multi-instance). */
    provisionedInstanceCount: z.number().int().nonnegative().optional(),
    /** Instance ceiling the host clamps launches to (android multi-instance). */
    maxGameInstances: z.number().int().positive().optional(),
  }),
  instances: z.array(gameInstanceHostReportSchema.extend({ gameInstanceIndex: gameInstanceIndexSchema })),
});

/** API → Host: a match request has been assigned to a specific instance. */
const matchAssignedPayloadSchema = z.object({
  matchRequestId: z.string(),
  gameInstanceIndex: gameInstanceIndexSchema,
  players: z.array(matchmakingPlayerSchema),
  steamCredentials: z
    .array(
      z.object({
        playerId: z.string(),
        username: z.string(),
        password: z.string(),
      }),
    )
    .optional(),
  playerContracts: z
    .array(
      z.object({
        playerId: z.string(),
        contract: z.string(),
      }),
    )
    .optional(),
});

/** Host → API: the instance is ready for the assigned players to connect. */
const hostMatchReadyPayloadSchema = z.object({
  matchRequestId: z.string(),
  gameInstanceIndex: gameInstanceIndexSchema,
  hostPeerId: z.string(),
});

/** API → Host: switch campaign (whole machine, or one instance when gameInstanceIndex is set). */
const campaignSwitchPayloadSchema = z.object({
  campaignId: z.string(),
  reason: z.string(),
  gameInstanceIndex: gameInstanceIndexSchema.optional(),
});

/** Host → API: the assigned match's instance has been released (last player left, or eviction). */
const matchEndedPayloadSchema = z.object({
  matchRequestId: z.string(),
  gameInstanceIndex: gameInstanceIndexSchema,
  reason: z.string(),
});

/** API → Host: ops-driven capacity push — override the host's local maxPlayers ceiling. */
const hostCapacityConfigPayloadSchema = z.object({
  maxPlayers: z.number().int().min(1).max(64),
});

/** API → Host: admin-initiated restart of a single game instance (production-readiness Phase 1). */
const adminRestartInstancePayloadSchema = z.object({
  gameInstanceIndex: gameInstanceIndexSchema,
  reason: z.string().optional(),
});

// =============================================================================
// Payload schemas — Player-facing (strict: no instance identity, ever)
// =============================================================================

/** Player → API: v1 MATCHMAKER_CREATE_INVITATION semantics. */
const createMatchRequestPayloadSchema = z
  .object({
    playerInfo: matchmakingPlayerSchema,
    sessionPreset: matchmakingSessionPresetSchema,
    matchmakeToPublicMachine: z.boolean(),
    campaignId: z.string().optional(),
    targetMachineId: z.string().optional(),
    searchPattern: z.array(z.string()).optional(),
  })
  .strict();

const joinMatchRequestPayloadSchema = z
  .object({
    matchRequestId: z.string(),
    playerInfo: matchmakingPlayerSchema,
  })
  .strict();

const leaveMatchRequestPayloadSchema = z
  .object({
    matchRequestId: z.string(),
    playerId: z.string(),
  })
  .strict();

const addToQueuePayloadSchema = z
  .object({
    matchRequestId: z.string(),
    useSteamGuestLogin: z.boolean().optional(),
    regionOverride: z.string().optional(),
  })
  .strict();

const matchRequestCreatedPayloadSchema = z
  .object({
    matchRequestId: z.string(),
  })
  .strict();

const matchRequestUpdatedPayloadSchema = z
  .object({
    matchRequestId: z.string(),
    players: z.array(matchmakingPlayerSchema),
  })
  .strict();

const matchFoundPayloadSchema = z
  .object({
    matchRequestId: z.string(),
    hostBroadcastId: z.string().optional(),
    estimatedSetupTimeSeconds: z.number().optional(),
  })
  .strict();

const matchReadyPayloadSchema = z
  .object({
    matchRequestId: z.string(),
    broadcastId: z.string(),
  })
  .strict();

const matchEndPayloadSchema = z
  .object({
    matchRequestId: z.string(),
    reason: z.string(),
  })
  .strict();

const matchFailedPayloadSchema = z
  .object({
    matchRequestId: z.string(),
    reason: z.string(),
    canRetry: z.boolean(),
  })
  .strict();

const queueStatusPayloadSchema = z
  .object({
    matchRequestId: z.string(),
    position: z.number(),
    estimatedWaitTimeSeconds: z.number().optional(),
    playersInQueue: z.number().optional(),
    hostsAvailable: z.number().optional(),
    noHostsAvailable: z.boolean().optional(),
  })
  .strict();

const errorPayloadSchema = z
  .object({
    matchRequestId: z.string().optional(),
    error: z.object({
      code: z.string(),
      message: z.string(),
    }),
  })
  .strict();

const stateUpdateDetailsSchema = z.object({
  reason: z.string().optional(),
  activeMatchRequestId: z.string().optional(),
  matchmakeable: z.boolean().optional(),
  timestamp: z.number().optional(),
  campaignId: z.string().optional(),
  isCloudHost: z.boolean().optional(),
});

const stateUpdatePayloadSchema = z
  .object({
    matchRequestId: z.string().optional(),
    state: z.enum(['MATCHMAKING_DISABLED', 'REGISTERED', 'PREFERENCES_UPDATED', 'IDLE']),
    stateDetails: stateUpdateDetailsSchema.optional(),
  })
  .strict();

// =============================================================================
// Full message schemas
// =============================================================================

const hostStateReportSchema = baseMessageSchema
  .extend({ type: z.literal(MatchmakingV2MessageType.MATCHMAKING_V2_HOST_STATE_REPORT) })
  .merge(hostStateReportPayloadSchema);
const matchAssignedSchema = baseMessageSchema
  .extend({ type: z.literal(MatchmakingV2MessageType.MATCHMAKING_V2_MATCH_ASSIGNED) })
  .merge(matchAssignedPayloadSchema);
const hostMatchReadySchema = baseMessageSchema
  .extend({ type: z.literal(MatchmakingV2MessageType.MATCHMAKING_V2_HOST_MATCH_READY) })
  .merge(hostMatchReadyPayloadSchema);
const campaignSwitchSchema = baseMessageSchema
  .extend({ type: z.literal(MatchmakingV2MessageType.MATCHMAKING_V2_CAMPAIGN_SWITCH) })
  .merge(campaignSwitchPayloadSchema);
const matchEndedSchema = baseMessageSchema
  .extend({ type: z.literal(MatchmakingV2MessageType.MATCHMAKING_V2_MATCH_ENDED) })
  .merge(matchEndedPayloadSchema);
const hostCapacityConfigSchema = baseMessageSchema
  .extend({ type: z.literal(MatchmakingV2MessageType.MATCHMAKING_V2_HOST_CAPACITY_CONFIG) })
  .merge(hostCapacityConfigPayloadSchema);
const adminRestartInstanceSchema = baseMessageSchema
  .extend({ type: z.literal(MatchmakingV2MessageType.MATCHMAKING_V2_ADMIN_RESTART_INSTANCE) })
  .merge(adminRestartInstancePayloadSchema);
const createMatchRequestSchema = baseMessageSchema
  .extend({ type: z.literal(MatchmakingV2MessageType.MATCHMAKING_V2_CREATE_MATCH_REQUEST) })
  .merge(createMatchRequestPayloadSchema);
const joinMatchRequestSchema = baseMessageSchema
  .extend({ type: z.literal(MatchmakingV2MessageType.MATCHMAKING_V2_JOIN_MATCH_REQUEST) })
  .merge(joinMatchRequestPayloadSchema);
const leaveMatchRequestSchema = baseMessageSchema
  .extend({ type: z.literal(MatchmakingV2MessageType.MATCHMAKING_V2_LEAVE_MATCH_REQUEST) })
  .merge(leaveMatchRequestPayloadSchema);
const addToQueueSchema = baseMessageSchema
  .extend({ type: z.literal(MatchmakingV2MessageType.MATCHMAKING_V2_ADD_TO_QUEUE) })
  .merge(addToQueuePayloadSchema);
const matchRequestCreatedSchema = baseMessageSchema
  .extend({ type: z.literal(MatchmakingV2MessageType.MATCHMAKING_V2_MATCH_REQUEST_CREATED) })
  .merge(matchRequestCreatedPayloadSchema);
const matchRequestUpdatedSchema = baseMessageSchema
  .extend({ type: z.literal(MatchmakingV2MessageType.MATCHMAKING_V2_MATCH_REQUEST_UPDATED) })
  .merge(matchRequestUpdatedPayloadSchema);
const matchFoundSchema = baseMessageSchema
  .extend({ type: z.literal(MatchmakingV2MessageType.MATCHMAKING_V2_MATCH_FOUND) })
  .merge(matchFoundPayloadSchema);
const matchReadySchema = baseMessageSchema
  .extend({ type: z.literal(MatchmakingV2MessageType.MATCHMAKING_V2_MATCH_READY) })
  .merge(matchReadyPayloadSchema);
const matchEndSchema = baseMessageSchema
  .extend({ type: z.literal(MatchmakingV2MessageType.MATCHMAKING_V2_MATCH_END) })
  .merge(matchEndPayloadSchema);
const matchFailedSchema = baseMessageSchema
  .extend({ type: z.literal(MatchmakingV2MessageType.MATCHMAKING_V2_MATCH_FAILED) })
  .merge(matchFailedPayloadSchema);
const queueStatusSchema = baseMessageSchema
  .extend({ type: z.literal(MatchmakingV2MessageType.MATCHMAKING_V2_QUEUE_STATUS) })
  .merge(queueStatusPayloadSchema);
const errorSchema = baseMessageSchema
  .extend({ type: z.literal(MatchmakingV2MessageType.MATCHMAKING_V2_ERROR) })
  .merge(errorPayloadSchema);
const stateUpdateSchema = baseMessageSchema
  .extend({ type: z.literal(MatchmakingV2MessageType.MATCHMAKING_V2_STATE_UPDATE) })
  .merge(stateUpdatePayloadSchema);

export const matchmakingV2MessageSchema = z.discriminatedUnion('type', [
  hostStateReportSchema,
  matchAssignedSchema,
  hostMatchReadySchema,
  campaignSwitchSchema,
  matchEndedSchema,
  hostCapacityConfigSchema,
  adminRestartInstanceSchema,
  createMatchRequestSchema,
  joinMatchRequestSchema,
  leaveMatchRequestSchema,
  addToQueueSchema,
  matchRequestCreatedSchema,
  matchRequestUpdatedSchema,
  matchFoundSchema,
  matchReadySchema,
  matchEndSchema,
  matchFailedSchema,
  queueStatusSchema,
  errorSchema,
  stateUpdateSchema,
]);

// =============================================================================
// Inferred types
// =============================================================================

export type MatchmakingV2HostStateReportPayload = z.infer<typeof hostStateReportPayloadSchema>;
export type MatchmakingV2MatchAssignedPayload = z.infer<typeof matchAssignedPayloadSchema>;
export type MatchmakingV2HostMatchReadyPayload = z.infer<typeof hostMatchReadyPayloadSchema>;
export type MatchmakingV2CampaignSwitchPayload = z.infer<typeof campaignSwitchPayloadSchema>;
export type MatchmakingV2MatchEndedPayload = z.infer<typeof matchEndedPayloadSchema>;
export type MatchmakingV2HostCapacityConfigPayload = z.infer<typeof hostCapacityConfigPayloadSchema>;
export type MatchmakingV2AdminRestartInstancePayload = z.infer<typeof adminRestartInstancePayloadSchema>;
export type MatchmakingV2CreateMatchRequestPayload = z.infer<typeof createMatchRequestPayloadSchema>;
export type MatchmakingV2JoinMatchRequestPayload = z.infer<typeof joinMatchRequestPayloadSchema>;
export type MatchmakingV2LeaveMatchRequestPayload = z.infer<typeof leaveMatchRequestPayloadSchema>;
export type MatchmakingV2AddToQueuePayload = z.infer<typeof addToQueuePayloadSchema>;
export type MatchmakingV2MatchRequestCreatedPayload = z.infer<typeof matchRequestCreatedPayloadSchema>;
export type MatchmakingV2MatchRequestUpdatedPayload = z.infer<typeof matchRequestUpdatedPayloadSchema>;
export type MatchmakingV2MatchFoundPayload = z.infer<typeof matchFoundPayloadSchema>;
export type MatchmakingV2MatchReadyPayload = z.infer<typeof matchReadyPayloadSchema>;
export type MatchmakingV2MatchEndPayload = z.infer<typeof matchEndPayloadSchema>;
export type MatchmakingV2MatchFailedPayload = z.infer<typeof matchFailedPayloadSchema>;
export type MatchmakingV2QueueStatusPayload = z.infer<typeof queueStatusPayloadSchema>;
export type MatchmakingV2ErrorPayload = z.infer<typeof errorPayloadSchema>;
export type MatchmakingV2StateUpdatePayload = z.infer<typeof stateUpdatePayloadSchema>;

export type MatchmakingV2Message = z.infer<typeof matchmakingV2MessageSchema>;

// PayloadType mapping
type PayloadType<T extends MatchmakingV2MessageType> = T extends MatchmakingV2MessageType.MATCHMAKING_V2_HOST_STATE_REPORT
  ? MatchmakingV2HostStateReportPayload
  : T extends MatchmakingV2MessageType.MATCHMAKING_V2_MATCH_ASSIGNED
    ? MatchmakingV2MatchAssignedPayload
    : T extends MatchmakingV2MessageType.MATCHMAKING_V2_HOST_MATCH_READY
      ? MatchmakingV2HostMatchReadyPayload
      : T extends MatchmakingV2MessageType.MATCHMAKING_V2_CAMPAIGN_SWITCH
        ? MatchmakingV2CampaignSwitchPayload
        : T extends MatchmakingV2MessageType.MATCHMAKING_V2_MATCH_ENDED
          ? MatchmakingV2MatchEndedPayload
          : T extends MatchmakingV2MessageType.MATCHMAKING_V2_CREATE_MATCH_REQUEST
            ? MatchmakingV2CreateMatchRequestPayload
            : T extends MatchmakingV2MessageType.MATCHMAKING_V2_JOIN_MATCH_REQUEST
              ? MatchmakingV2JoinMatchRequestPayload
              : T extends MatchmakingV2MessageType.MATCHMAKING_V2_LEAVE_MATCH_REQUEST
                ? MatchmakingV2LeaveMatchRequestPayload
                : T extends MatchmakingV2MessageType.MATCHMAKING_V2_ADD_TO_QUEUE
                  ? MatchmakingV2AddToQueuePayload
                  : T extends MatchmakingV2MessageType.MATCHMAKING_V2_MATCH_REQUEST_CREATED
                    ? MatchmakingV2MatchRequestCreatedPayload
                    : T extends MatchmakingV2MessageType.MATCHMAKING_V2_MATCH_REQUEST_UPDATED
                      ? MatchmakingV2MatchRequestUpdatedPayload
                      : T extends MatchmakingV2MessageType.MATCHMAKING_V2_MATCH_FOUND
                        ? MatchmakingV2MatchFoundPayload
                        : T extends MatchmakingV2MessageType.MATCHMAKING_V2_MATCH_READY
                          ? MatchmakingV2MatchReadyPayload
                          : T extends MatchmakingV2MessageType.MATCHMAKING_V2_MATCH_END
                            ? MatchmakingV2MatchEndPayload
                            : T extends MatchmakingV2MessageType.MATCHMAKING_V2_MATCH_FAILED
                              ? MatchmakingV2MatchFailedPayload
                              : T extends MatchmakingV2MessageType.MATCHMAKING_V2_QUEUE_STATUS
                                ? MatchmakingV2QueueStatusPayload
                                : T extends MatchmakingV2MessageType.MATCHMAKING_V2_ERROR
                                  ? MatchmakingV2ErrorPayload
                                  : T extends MatchmakingV2MessageType.MATCHMAKING_V2_STATE_UPDATE
                                    ? MatchmakingV2StateUpdatePayload
                                    : T extends MatchmakingV2MessageType.MATCHMAKING_V2_HOST_CAPACITY_CONFIG
                                      ? MatchmakingV2HostCapacityConfigPayload
                                      : T extends MatchmakingV2MessageType.MATCHMAKING_V2_ADMIN_RESTART_INSTANCE
                                        ? MatchmakingV2AdminRestartInstancePayload
                                        : never;

/**
 * Generate a matchmaking v2 message with the correct type and payload
 */
export function generateMatchmakingV2Message<T extends MatchmakingV2MessageType>(
  type: T,
  payload: PayloadType<T>,
): MatchmakingV2Message {
  const base = {
    id: createMessageId(),
    timestamp: Date.now(),
    type,
  };

  switch (type) {
    case MatchmakingV2MessageType.MATCHMAKING_V2_HOST_STATE_REPORT:
      return hostStateReportSchema.parse({ ...base, ...payload });
    case MatchmakingV2MessageType.MATCHMAKING_V2_MATCH_ASSIGNED:
      return matchAssignedSchema.parse({ ...base, ...payload });
    case MatchmakingV2MessageType.MATCHMAKING_V2_HOST_MATCH_READY:
      return hostMatchReadySchema.parse({ ...base, ...payload });
    case MatchmakingV2MessageType.MATCHMAKING_V2_CAMPAIGN_SWITCH:
      return campaignSwitchSchema.parse({ ...base, ...payload });
    case MatchmakingV2MessageType.MATCHMAKING_V2_MATCH_ENDED:
      return matchEndedSchema.parse({ ...base, ...payload });
    case MatchmakingV2MessageType.MATCHMAKING_V2_HOST_CAPACITY_CONFIG:
      return hostCapacityConfigSchema.parse({ ...base, ...payload });
    case MatchmakingV2MessageType.MATCHMAKING_V2_ADMIN_RESTART_INSTANCE:
      return adminRestartInstanceSchema.parse({ ...base, ...payload });
    case MatchmakingV2MessageType.MATCHMAKING_V2_CREATE_MATCH_REQUEST:
      return createMatchRequestSchema.parse({ ...base, ...payload });
    case MatchmakingV2MessageType.MATCHMAKING_V2_JOIN_MATCH_REQUEST:
      return joinMatchRequestSchema.parse({ ...base, ...payload });
    case MatchmakingV2MessageType.MATCHMAKING_V2_LEAVE_MATCH_REQUEST:
      return leaveMatchRequestSchema.parse({ ...base, ...payload });
    case MatchmakingV2MessageType.MATCHMAKING_V2_ADD_TO_QUEUE:
      return addToQueueSchema.parse({ ...base, ...payload });
    case MatchmakingV2MessageType.MATCHMAKING_V2_MATCH_REQUEST_CREATED:
      return matchRequestCreatedSchema.parse({ ...base, ...payload });
    case MatchmakingV2MessageType.MATCHMAKING_V2_MATCH_REQUEST_UPDATED:
      return matchRequestUpdatedSchema.parse({ ...base, ...payload });
    case MatchmakingV2MessageType.MATCHMAKING_V2_MATCH_FOUND:
      return matchFoundSchema.parse({ ...base, ...payload });
    case MatchmakingV2MessageType.MATCHMAKING_V2_MATCH_READY:
      return matchReadySchema.parse({ ...base, ...payload });
    case MatchmakingV2MessageType.MATCHMAKING_V2_MATCH_END:
      return matchEndSchema.parse({ ...base, ...payload });
    case MatchmakingV2MessageType.MATCHMAKING_V2_MATCH_FAILED:
      return matchFailedSchema.parse({ ...base, ...payload });
    case MatchmakingV2MessageType.MATCHMAKING_V2_QUEUE_STATUS:
      return queueStatusSchema.parse({ ...base, ...payload });
    case MatchmakingV2MessageType.MATCHMAKING_V2_ERROR:
      return errorSchema.parse({ ...base, ...payload });
    case MatchmakingV2MessageType.MATCHMAKING_V2_STATE_UPDATE:
      return stateUpdateSchema.parse({ ...base, ...payload });
    default: {
      const exhaustiveCheck: never = type;
      throw new Error(`Unhandled message type: ${exhaustiveCheck}`);
    }
  }
}
